import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Box from '@mui/material/Box'

interface CrmPageChromeContextValue {
  setSlotEl: (el: HTMLElement | null) => void
  slotEl: HTMLElement | null
}

const CrmPageChromeContext = createContext<CrmPageChromeContextValue | undefined>(undefined)

export const CrmPageChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slotEl, setSlotElState] = useState<HTMLElement | null>(null)
  const setSlotEl = useCallback((el: HTMLElement | null) => {
    setSlotElState(prev => (prev === el ? prev : el))
  }, [])
  const value = useMemo(() => ({ slotEl, setSlotEl }), [slotEl, setSlotEl])
  return <CrmPageChromeContext.Provider value={value}>{children}</CrmPageChromeContext.Provider>
}

const useCrmPageChrome = (): CrmPageChromeContextValue => {
  const ctx = useContext(CrmPageChromeContext)
  if (!ctx) {
    throw new Error('Crm page chrome must be used within CrmPageChromeProvider')
  }
  return ctx
}

/** Mount point in the page title row — tabs portal their actions here. */
export const CrmPageActionsOutlet: React.FC = () => {
  const { setSlotEl } = useCrmPageChrome()
  return (
    <Box
      ref={(el: HTMLDivElement | null) => setSlotEl(el)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 0.75,
        minWidth: 0,
        '&:empty': { display: 'none' },
      }}
    />
  )
}

/** Portals tab toolbar actions into the Lead Management title row next to the (i) icon. */
export const CrmPageActions: React.FC<{ active?: boolean; children: React.ReactNode }> = ({
  active = true,
  children,
}) => {
  const { slotEl } = useCrmPageChrome()
  if (!active || !slotEl) return null
  return createPortal(children, slotEl)
}
